import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { brandColors } from '@config/brandTokens';
import { useAuthStore } from '@store/authStore';
import { fetchMySubscription } from '@services/subscriptionService';
import {
  DEFAULT_SUBSCRIPTION_OFFERS,
  fetchSubscriptionOffers,
  formatCatalogYearly,
  type SubscriptionOffer,
} from '@/content/subscriptionPricing';
import {
  audienceLabelForRole,
  familyPlanSummary,
  inAppPlansPath,
  inAppPurchasePath,
  isOccupiedSubscription,
  offersForAuthenticatedUser,
  subscriptionStatusLabel,
} from '@/content/subscriptionCatalog';
import type { UserSubscription } from '@types/index';

function formatExpiry(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

interface AccountSubscriptionCardProps {
  academyType?: string | null;
}

/** Compact post-login subscription status — CTAs stay in the dashboard. */
const AccountSubscriptionCard: React.FC<AccountSubscriptionCardProps> = ({ academyType }) => {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const [subscription, setSubscription] = useState<UserSubscription | null>(user?.subscription || null);
  const [offers, setOffers] = useState<SubscriptionOffer[]>(DEFAULT_SUBSCRIPTION_OFFERS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [mine, offerList] = await Promise.all([fetchMySubscription(), fetchSubscriptionOffers()]);
      if (cancelled) return;
      if (offerList.length) setOffers(offerList);
      if (mine) {
        setSubscription(mine);
        if (user) setUser({ ...user, subscription: mine });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const role = String(user?.role || '');
  if (
    !user ||
    user.isSuperAdmin ||
    ['admin', 'super_admin', 'evaluation_admin', 'contest_admin', 'support_admin', 'sponsor'].includes(role)
  ) {
    return null;
  }

  const relevant = offersForAuthenticatedUser(offers, role, academyType);
  const offer =
    relevant.find((o) => o.id === (subscription?.offerId || subscription?.suggestedOfferId)) || relevant[0];
  const occupied = isOccupiedSubscription(subscription?.status);
  const active = subscription?.status === 'active';
  const expiry = formatExpiry(subscription?.expiresAt);
  const plansPath = inAppPlansPath(role);
  const purchasePath = inAppPurchasePath(role);

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: brandColors.white,
        border: '1px solid #e2e8f0',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.35 }}>
            <Typography
              sx={{
                fontWeight: 800,
                color: brandColors.navy,
                fontFamily: '"Sora", Arial, sans-serif',
                fontSize: '0.95rem',
              }}
            >
              Subscription
            </Typography>
            <Chip
              size="small"
              label={subscriptionStatusLabel(subscription?.status)}
              sx={{
                height: 22,
                fontWeight: 800,
                bgcolor:
                  subscription?.status === 'prebooked'
                    ? '#fff7ed'
                    : active || subscription?.status === 'beta'
                      ? '#ecfdf5'
                      : brandColors.ice,
                color:
                  subscription?.status === 'prebooked'
                    ? '#c2410c'
                    : active || subscription?.status === 'beta'
                      ? '#047857'
                      : brandColors.navy,
              }}
            />
          </Stack>
          <Typography variant="body2" sx={{ color: brandColors.slate, lineHeight: 1.45 }}>
            {occupied
              ? `${offer?.name || audienceLabelForRole(role, academyType)}${
                  subscription?.planConfig ? ` · ${familyPlanSummary(subscription.planConfig)}` : ''
                }${
                  subscription?.amountUsd != null
                    ? ` · ${formatCatalogYearly(subscription.amountUsd, subscription.currency || 'USD')} / year`
                    : ''
                }${expiry ? ` · until ${expiry}` : ''}`
              : 'No yearly plan on this account yet. View plans on your dashboard when you are ready.'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(plansPath)}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderColor: brandColors.navy,
              color: brandColors.navy,
            }}
          >
            View plans
          </Button>
          {!occupied && user.canEditProfile !== false && (
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(purchasePath)}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                bgcolor: brandColors.orange,
                '&:hover': { bgcolor: '#f07a3d' },
              }}
            >
              Purchase options
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default AccountSubscriptionCard;
